import React, {ReactNode} from 'react';
import styled, {css} from 'styled-components';

import {Renderer} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {TooltipCoordinator} from '../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {fontSizes} from '../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../libs/shared/ui/src/styles/mixins';

/*
 * Props.
 */

export interface SettingsFieldLineProps {
  title: ReactNode;
  renderTooltip?: Renderer;
  isTooltipMultiline?: boolean;
  subtitle?: ReactNode;
  isSubtitleMultiline?: boolean;
  content: ReactNode;
  className?: string;
  hasVerticalLayout?: boolean;
  hasNoBorder?: boolean;
}

/*
 * Style.
 */

const StyledDiv = styled.div<{$hasVerticalLayout?: boolean; $hasNoBorder?: boolean}>`
  display: grid;
  ${(p) => addGridLayoutStyles(p.$hasVerticalLayout)}

  border-top: ${(p) => !p.$hasNoBorder && `1px solid #e6e6e6`};
  padding-top: 19px;
  padding-bottom: 19px;
`;

function addGridLayoutStyles(hasVerticalLayout?: boolean) {
  if (!hasVerticalLayout) {
    return css`
      grid-template-areas:
        'title content'
        'subtitle content';
      grid-template-columns: 1fr auto;
      grid-template-rows: repeat(2, auto);
      align-items: center;
      grid-column-gap: 10px;
      grid-row-gap: 6px;
    `;
  }

  return css`
    grid-template-areas:
      'title'
      '.'
      'subtitle'
      '.'
      'content';
    grid-template-columns: 1fr;
    grid-template-rows: auto 10px auto 20px auto;
    justify-items: start;
  `;
}

const StyledTitleDiv = styled.div`
  grid-area: title;
  font-size: ${fontSizes.large};
  line-height: 1;
  color: ${(p) => p.theme.greys.shade90};
  display: flex;
  align-items: center;
`;

const StyledEmptySubtitleDiv = styled.div`
  margin-top: -10px;
`;

const StyledSubtitleDiv = styled.div<{$isSubtitleMultiline?: boolean}>`
  grid-area: subtitle;
  font-size: ${fontSizes.medium};
  line-height: 1.7;
  color: ${(p) => p.theme.greys.shade60};
  ${(p) => (!p.$isSubtitleMultiline ? ellipsis() : '')};
`;

const StyledContentDiv = styled.div<{$isFullWidth?: boolean}>`
  grid-area: content;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  ${(p) =>
    p.$isFullWidth &&
    css`
      width: 100%;
      align-items: flex-start;
    `}
`;

const StyledLabelIconDiv = styled.div`
  display: inline-block;
  padding-left: 6px;
  color: ${(p) => p.theme.greys.shade60};
`;

/*
 * Component.
 */

export const SettingsFieldLine = (props: SettingsFieldLineProps) => (
  <StyledDiv
    className={props.className}
    $hasVerticalLayout={props.hasVerticalLayout}
    $hasNoBorder={props.hasNoBorder}
  >
    <StyledTitleDiv>
      {props.title}
      {props.renderTooltip && (
        <StyledLabelIconDiv>
          <TooltipCoordinator
            render={props.renderTooltip}
            placement="top"
            isMultiline={props.isTooltipMultiline}
          />
        </StyledLabelIconDiv>
      )}
    </StyledTitleDiv>
    {props.subtitle ? (
      <StyledSubtitleDiv $isSubtitleMultiline={props.isSubtitleMultiline}>{props.subtitle}</StyledSubtitleDiv>
    ) : (
      <StyledEmptySubtitleDiv />
    )}
    <StyledContentDiv $isFullWidth={props.hasVerticalLayout}>{props.content}</StyledContentDiv>
  </StyledDiv>
);
